import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertOctagon,
  ArrowLeft,
  ArrowRight,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Logo } from '../../components/Logo';
import { useActiveBrand } from '../../api/brand';

const TICKET_ID = 'PS-7H3K-2X9F';

/**
 * 500 Server error — §10
 * Full-screen, centered, with retry + ticket reference.
 */
export function ServerError() {
  const brand = useActiveBrand();
  const [retrying, setRetrying] = useState(false);
  const [copied, setCopied] = useState(false);

  function tryAgain() {
    setRetrying(true);
    setTimeout(() => {
      setRetrying(false);
      window.location.reload();
    }, 1100);
  }

  function copyTicket() {
    navigator.clipboard?.writeText(TICKET_ID).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md text-center">
        <div className="flex flex-col items-center mb-7">
          <span
            aria-hidden="true"
            className="mono text-[88px] sm:text-[120px] leading-none font-semibold tracking-tighter text-zinc-200 dark:text-zinc-800 select-none"
          >
            500
          </span>
        </div>

        <div className="w-12 h-12 mx-auto -mt-12 mb-5 grid place-items-center rounded-full bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-700 shadow-sm text-rose-600 dark:text-rose-400 relative">
          <AlertOctagon className="w-5 h-5" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          Something went wrong.
        </h1>
        <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300 max-w-sm mx-auto">
          We've been notified and are looking into it. Please try again — most issues clear in
          a minute or two.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={tryAgain}
            disabled={retrying}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition disabled:opacity-50"
          >
            {retrying ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {retrying ? 'Trying again…' : 'Try again'}
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-zinc-900 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 bg-white/60 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Link>
        </div>

        {/* Ticket reference */}
        <div className="mt-8 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-xs">
          <span className="text-zinc-500 dark:text-zinc-400">Reference</span>
          <code className="mono font-medium text-zinc-800 dark:text-zinc-100 tabular-nums">
            {TICKET_ID}
          </code>
          <button
            type="button"
            onClick={copyTicket}
            className="ml-1 w-6 h-6 grid place-items-center rounded hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500 transition"
            title="Copy"
          >
            {copied ? (
              <span className="text-[10px] font-medium text-emerald-600">✓</span>
            ) : (
              <Copy className="w-3 h-3" />
            )}
          </button>
        </div>

        <p className="mt-6 text-xs text-zinc-500 dark:text-zinc-400">
          Persistent? Open a ticket{' '}
          <a
            href={`mailto:${brand.supportEmail}?subject=Error+${TICKET_ID}`}
            className="text-brand dark:text-brand-dm hover:underline inline-flex items-center gap-1"
          >
            with this reference
            <ArrowRight className="w-3 h-3" />
          </a>{' '}
          or check{' '}
          <a
            href={`https://status.${brand.domain}`}
            className="text-brand dark:text-brand-dm hover:underline inline-flex items-center gap-1"
          >
            status.{brand.domain}
            <ExternalLink className="w-3 h-3" />
          </a>
          .
        </p>

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Logo className="w-5 h-5 rounded-md" />
          <span>{brand.name}</span>
        </div>
      </div>
    </div>
  );
}
